import { createSelector } from 'reselect';
import { call, select } from 'redux-saga/effects';
import { get, logException, logMessage, pageContext } from 'ctm-util-functions';
import cloneDeep from 'lodash.clonedeep';

import {
    errorTrackingSelector,
    getProductIdsByAvailability,
    productInfoSelector,
    quoteIdSelector,
    resultsConfigSelector,
    selectedProductSelector,
    sortedAndFilteredProductsSelector,
    trackQuoteFormDataSelector,
    transactionPayloadSelector,
    verticalNameSelector,
    splitTrafficIdSelector,
    quoteFormSelector,
    ecommerceDataSelector,
    pageNameSelector
} from 'ctm-selectors';
import { getOfferDetails } from './state_selectors';
import { objectValuesToLowerCase } from './utils';
import { reporterLog } from './reporter';

/**
 * Get the ProductCategory state.
 * @param  {Object} state   - The application Redux store.
 * @returns {Object}         - The Product Category state.
 */
const productCategorySelector = (state) => {
    const productCategory = get(pageContext.getProps(), 'verticalSelectors.getProductCategory', false);
    if (productCategory) {
        return productCategory(state);
    }
    return null;
};

/**
 * HOF that creates a dynamic selector that takes extra arguments
 * @param {String} event - The name of the event to be triggered.
 * @param {String} eventType - ctm_event_type for the tracking.
 * @param {String} eventAction - ecommerce action, 'detail' or 'click'
 * @param {String} productId - the productId from the action for the product we are tracking
 * @returns {Function} - a reselect function that gets the tracking data for product call to action click
 */
const getTrackProductImpressionData = (event, eventType, eventAction, productId) => {
    return createSelector(
        [verticalNameSelector, productCategorySelector, selectedProductSelector, sortedAndFilteredProductsSelector, productInfoSelector],
        (verticalName, productCategory, selectedProduct, sortedAndFilteredProducts, productInfo) => {
            // If productId is present, use it to get the desired product for tracking, otherwise use selectedProductSelector
            if (productId) {
                // eslint-disable-next-line no-param-reassign
                selectedProduct = sortedAndFilteredProducts
                    .filter((product) => {
                        return product.getIn(['productMeta', 'id']) === productId;
                    })
                    .get(0);
            }
            const verticalTrackingSelectors = get(pageContext.getProps(), 'verticalSelectors.tracking');
            const verticalEcommerceProductValues = verticalTrackingSelectors ? verticalTrackingSelectors.ecommerce(selectedProduct) : {};
            const productName = selectedProduct.get('productName');
            const providerName = selectedProduct.get('providerName');
            return {
                event,
                ecommerce: {
                    ctm_event_type: eventType,
                    [eventAction]: {
                        actionField: {
                            list: verticalName
                        },
                        products: [
                            {
                                id: selectedProduct.getIn(['productMeta', 'id']),
                                name: productName && providerName ? `${providerName} - ${productName}` : null,
                                position: sortedAndFilteredProducts.indexOf(selectedProduct) + 1,
                                brand: selectedProduct.get('providerCode') || null,
                                category: productCategory,
                                ...verticalEcommerceProductValues
                            }
                        ]
                    }
                }
            };
        }
    );
};

/**
 * Reselect selector for getting data to track handover
 * @note: The redirection happens as a callback in the selector due to the implementation of GTM dataLayer
 */
export const getHandoverData = createSelector(
    [verticalNameSelector, productInfoSelector],
    (vertical, productInfo) => {
        const product = productInfo.get('product');
        const quoteUrl = product.get('quoteUrl');
        const productId = product.get('productId');
        // We are using localStorage as an intermediary here for cross-tab state.
        // The user could switch browsers, etc, so we extract the empty list by default if it's null.
        const data = JSON.parse(localStorage.getItem('trackingData'));
        const results = get(data, 'ecommerce.impressions', []);
        const products = results.filter((result) => result.id === productId);

        //  Schedule handover regardless of GTM state
        setTimeout(() => {
            if (quoteUrl) {
                window.location.replace(quoteUrl);
            } else {
                logException(new Error(`invalid handover url for product: ${productId}`));
            }
        }, 4500);

        return {
            ...data,
            event: 'PAGE_VIEW_EVENT',
            vertical,
            actionStep: `${vertical} handover`,
            ecommerce: {
                click: {
                    actionField: {
                        list: vertical
                    },
                    products
                }
            }
        };
    }
);

const trackMSATSDataRetrievalStatusSelector = createSelector(
    [quoteFormSelector, quoteIdSelector, transactionPayloadSelector],
    (quoteForm, quoteId, transaction) => {
        const results = {
            meterSource: quoteForm.getIn(['data', 'electricityMeterDetails', 'source']),
            nmiDataRetrievalStatus: quoteForm.getIn(['data', 'nmiDataRetrievalStatus'])
        };

        if (quoteId) {
            results.quoteId = quoteId;
        }

        return {
            transaction: transaction && transaction.toJS(),
            results
        };
    }
);

/**
 * Helper method to retrieve transaction object off payload.
 * @param {Immutable.Map} state The app state
 * @returns {Immutable.Map|false} Transaction data
 * @private
 */
const _getTransactionPayload = (state) => {
    const transaction = transactionPayloadSelector(state);
    return transaction && transaction.toJS();
};

/**
 * @desc Get transaction data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM.
 */
const getTransactionData = (state, event) => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    return { event, vertical, transaction: _getTransactionPayload(state) };
};

/**
 * @desc Get Page data including the event.
 * @param {Object} state    - The application state
 * @param {String} event    - The name of the event to be triggered
 * @param {String} actionStep - An optional actionStep (to override default) to include in the GTM event
 * @returns {Object} trackingData to be pushed to GTM.
 */
const getPageViewData = (state, event, actionStep = '') => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    const verticalFilter = productCategorySelector(state);
    const transaction = _getTransactionPayload(state);
    const formData = trackQuoteFormDataSelector(state);
    if (actionStep !== '') {
        formData.actionStep = vertical + actionStep;
    }
    return {
        event,
        vertical,
        verticalFilter,
        transaction,
        ...formData
    };
};

const getEcommerceData = (state, type) => {
    return { ...ecommerceDataSelector(type)(state) };
};

/**
 * @desc Get getTrackQuoteComparisonResultsData data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM
 */
const getTrackQuoteComparisonResultsData = (state, event) => {
    const selectedProductIds = resultsConfigSelector(state)
        .getIn(['filters', 'selectedProductIds'])
        .toJS();
    const brands = selectedProductIds.map((id) => (id.indexOf('-') !== -1 ? id.split('-')[0] : id));
    return { event, comparedProducts: { brands, selectedProductIds } };
};

/**
 * @desc Get getTrackQuoteNotProvidedData data including the event and vertical name
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object|Boolean} trackingData to be pushed to GTM
 */
const getTrackQuoteNotProvidedData = (state, event) => {
    const unavailableProducts = getProductIdsByAvailability(state, false).toJS();
    if (!unavailableProducts.length) {
        return false;
    }
    return { event, unavailableProducts };
};

/**
 * @desc Get the trackTransactionUpdate data.
 * @param {Object} state    - The application state
 * @param {String} event    - The name of the event to be triggered
 * @returns {Object} trackingData to be pushed to GTM
 */
const getErrorTracking = (state, event) => {
    const errors = errorTrackingSelector(state);
    return { event, errors };
};

/**
 * @desc Get getTrackMSATSDataRetrievalStatus data including the event.
 * @param {Object} state    - The application state.
 * @param {String} event    - The name of the event to be triggered.
 * @returns {Object} trackingData to be pushed to GTM
 */
const getTrackMSATSDataRetrievalStatus = (state, event) => {
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const vertical = get(settings, 'tracking.vertical', null);
    return { event, vertical, ...trackMSATSDataRetrievalStatusSelector(state) };
};

/**
 * Push the data to the Google Tag Manager.
 * More info in here: https://developers.google.com/tag-manager/quickstart
 * @param {Object} action The action details.
 * @param {Object} data The data to be pushed to google data layer.
 */
const recordGTM = function* (action, { event, ...data }) {
    if (typeof window.dataLayer === 'undefined') {
        // Log an error using the reporter.
        yield call(reporterLog, action, 'Google Data Layer is undefined');
    }
    const lowerCaseData = { event, ...objectValuesToLowerCase(cloneDeep(data)) };
    try {
        window.dataLayer.push(lowerCaseData);
    } catch (error) {
        Object.assign(error, { lowerCaseData });
        // Sentry error logging including the GTM data
        yield call(logException, error, 'error', { action });
    }
};

/**
 * @desc generator to catch all track actions, then get different data from selectors, then push to the dataLayer
 * @param {Object} action The dispatched action that contains the description of how to track data
 * @returns {Boolean} true if track successfully, otherwise false
 */
export const track = function* (action) {
    try {
        let data = null;
        let event = '';

        switch (action.type) {
            // 'SET_JOURNEY_PAYLOAD' = Whenever journey/transaction data gets updated
            case 'SET_JOURNEY_PAYLOAD': {
                event = 'trackTransactionUpdate';
                data = yield select(getTransactionData, event);
                break;
            }
            // 'SET_ROUTER_LOCATION' = Save on all route changes on "journey" pages
            case 'TRACK_INITIAL_FORM':
            case 'SET_RESULTS_PAYLOAD':
            case 'SET_RESULTS_PAYLOAD_HNC':
            case 'SET_ROUTER_LOCATION': {
                const pageName = yield select(pageNameSelector);
                const verticalName = yield select(verticalNameSelector);

                // avoid triggering the page view event twice on the car verify page
                if (verticalName === 'car' && pageName === 'verify') return false;

                // Only fire an event on the results page if we've actually fetched the results.
                // Special case for H&C as there is additional logic on the SET_RESULTS_PAYLOAD_HNC action.
                if (pageContext.getVertical() === 'home-contents-insurance'
                    ? pageName === 'results' && action.type !== 'SET_RESULTS_PAYLOAD_HNC'
                    : pageName === 'results' && action.type !== 'SET_RESULTS_PAYLOAD'
                ) return false;
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event);
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };

                if (pageName !== 'handover') localStorage.setItem('trackingData', JSON.stringify(data));

                // on first page load, the transaction details won't be ready yet so we don't want to track the event yet
                // it will be triggered manually after we record the transaction details
                const transaction = yield select(transactionPayloadSelector);
                // Exception for anonymous prefill pages as no journey (transaction object) exists at this point
                if (!transaction && (action.name !== 'prefill_check' && action.name !== 'prefill_verify')) return false;
                // Block the page view event on the prefill verify pages, these will be manually dispatched from their respective components
                if (pageName === 'prefill_verify' &&  (data.actionStep === 'car prefill verify' || data.actionStep === 'home_contents prefill verify')) return false;

                break;
            }
            // 'TRACK_UNAVAILABLE_QUOTES' = When retrieving results successfully and there are no quotes.
            case 'TRACK_UNAVAILABLE_QUOTES': {
                event = 'trackQuoteNotProvided';
                data = yield select(getTrackQuoteNotProvidedData, event);
                // If they're all available, don't run this call.
                if (data === false) {
                    return false;
                }
                break;
            }
            case 'TOGGLE_COMPARE_MODE': {
                if (action.toggle === true) {
                    event = 'trackQuoteComparison';
                    data = yield select(getTrackQuoteComparisonResultsData, event);
                } else {
                    return false;
                }
                break;
            }

            // 'VALIDATE_PAGE' = When the page is being validated
            case 'VALIDATE_PAGE': {
                event = 'errorTracking';
                data = yield select(getErrorTracking, event);
                if (data.errors.length === 0) return false;
                break;
            }
            case 'CALL_DIRECT':
            case 'TRACK_CALL_ME_BACK': {
                event = 'PAGE_VIEW_EVENT';
                const productId = get(action, 'requestPayload.productId', action.productId);
                const trackProductImpressionData = yield select(getTrackProductImpressionData(event, action.type.replace('TRACK_', ''), 'click', productId));
                const verticalName = yield select(verticalNameSelector);
                data = { actionStep: `${verticalName} handover`, ...trackProductImpressionData };
                break;
            }
            // 'POSTCODE_NOT_SUPPORTED' = when user enters a postcode that TWLD does not support
            case 'POSTCODE_NOT_SUPPORTED': {
                event = 'trackQuoteEvent';
                data = { event, action: 'location not available' };
                break;
            }
            case 'HANDOVER_TO_PARTNER': {
                event = 'PAGE_VIEW_EVENT';
                data = yield select(getHandoverData);
                break;
            }
            // 'TRACK_MSATS_RETRIEVAL_STATUS' = when MSATS data retrieval status in results when MSATS is available
            case 'TRACK_MSATS_RETRIEVAL_STATUS': {
                // do not track the status if not in MSATS a/b testing test version
                const splitTrafficId = yield select(splitTrafficIdSelector);

                if (splitTrafficId !== 'msats') {
                    return false;
                }

                event = 'trackNmiDataRetrieval';
                data = yield select(getTrackMSATSDataRetrievalStatus, event);
                break;
            }
            case 'OFFER_SHOWN': {
                event = 'OFFER_EVENT';
                const offerDetailsFromPayload = yield select(getOfferDetails);
                const offerDetails = offerDetailsFromPayload.offers[0].offer;
                // RPO banner to fire dispatch once RPO banner is mounted;
                data = { event, offerDetails };
                break;
            }
            case 'ELEVEN_MONTH_QUOTE_START': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' quote start');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'ELEVEN_MONTH_RESULTS': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' results');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'ELEVEN_MONTH_VERIFY': {
                const verticalName = yield select(verticalNameSelector);
                const pageName = yield select(pageNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    transaction: { journeyId: action.journeyId },
                    vertical: verticalName,
                    actionStep: `${verticalName} ${pageName}`
                };
                break;
            }
            case 'PREFILL_VERIFY_FAIL': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill verify failed`
                };
                break;
            }
            case 'PREFILL_LOCKOUT': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill lockout`
                };
                break;
            }
            case 'PREFILL_CHECK_SUCCESS': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill check success`
                };
                break;
            }
            case 'PREFILL_CHECK_FAILED': {
                const verticalName = yield select(verticalNameSelector);
                data = {
                    event: 'PAGE_VIEW_EVENT',
                    vertical: verticalName,
                    actionStep: `${verticalName} prefill check failed`
                };
                break;
            }
            case 'PREFILL_QUOTE_START': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' quote start');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;
                break;
            }
            case 'PREFILL_VERIFY': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' prefill verify');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };

                break;
            }
            case 'PREFILL_SAVED_RESULTS': {
                event = 'PAGE_VIEW_EVENT';
                const pageData = yield select(getPageViewData, event, ' results');
                const ecommerceData = yield select(getEcommerceData, action.type);
                data = { ...pageData, ...ecommerceData };
                const transaction = yield select(transactionPayloadSelector);
                if (!transaction) return false;

                break;
            }
            default: {
                // Do not track
                return false;
            }
        }
        yield call(recordGTM, action, data);
        yield call(logMessage, `${event} pushed to GTM`, data);
    } catch (error) {
        // Log the error using the reporter.
        yield call(reporterLog, action, error);
        // Sentry error logging
        yield call(logException, error, 'error', { action });
    }

    return true;
};
